import { useState, useEffect, useRef } from 'react'
import {
  Send, Loader2, Bot, User, RefreshCw, AlertCircle,
  FileText, CheckSquare, Square, ChevronDown, X, Sparkles, Zap, Brain
} from 'lucide-react'
import { getAuthToken } from '@/hooks/useAuth'
import { useTenant } from '@/hooks/useTenant'
import {
  AI_PROVIDERS,
  REASONING_STYLES,
  PROVIDER_STYLES,
  getProviderFromModel,
  getProviderInfo,
  getReasoningLevelsForProvider,
  getDefaultReasoningForProvider,
} from '@/config/models'
import { API_BASE } from '@/services/api'

interface Document {
  document_id: string
  name: string
  filename: string
  description: string
  file_type: string
  category: string
  status: string
  size_bytes: number
  chunks_count?: number
  created_at: string
}

interface Source {
  file_id: string
  filename?: string
  score?: number
  excerpt?: string
}

interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  sources?: Source[]
  model?: string
  actualModel?: string
  timestamp: Date
}

export default function KBChatInterface() {
  const { tenantId } = useTenant()
  const [documents, setDocuments] = useState<Document[]>([])
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([])
  const [allDocsSelected, setAllDocsSelected] = useState(true)
  const [showDocSelector, setShowDocSelector] = useState(false)

  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [selectedModel, setSelectedModel] = useState('gpt-5-mini')
  const [selectedReasoning, setSelectedReasoning] = useState('medium')
  const [isLoading, setIsLoading] = useState(false)
  const [loadingDocs, setLoadingDocs] = useState(false)
  const [error, setError] = useState('')

  const messagesEndRef = useRef<HTMLDivElement>(null)

  const currentProviderId = getProviderFromModel(selectedModel)
  const currentProvider = getProviderInfo(currentProviderId)
  const providerStyles = PROVIDER_STYLES[currentProviderId] || PROVIDER_STYLES.openai
  const reasoningLevels = getReasoningLevelsForProvider(currentProviderId)

  const getHeaders = async (): Promise<Record<string, string>> => {
    const token = await getAuthToken()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Tenant-Id': tenantId || '',
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    return headers
  }

  useEffect(() => {
    loadDocuments()
  }, [tenantId])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const loadDocuments = async () => {
    if (!tenantId) return

    setLoadingDocs(true)
    setError('')
    try {
      const headers = await getHeaders()
      const response = await fetch(`${API_BASE}/v1/knowledge-base/documents`, { headers })

      if (!response.ok) throw new Error('Failed to load documents')

      const data = await response.json()
      const docs = data.documents || []
      setDocuments(docs.filter((d: Document) => d.status === 'ready'))
    } catch (err: any) {
      setError('Error loading documents')
      console.error(err)
    } finally {
      setLoadingDocs(false)
    }
  }

  const toggleDocSelection = (docId: string) => {
    if (allDocsSelected) {
      setAllDocsSelected(false)
      setSelectedDocIds([docId])
    } else {
      setSelectedDocIds(prev =>
        prev.includes(docId)
          ? prev.filter(id => id !== docId)
          : [...prev, docId]
      )
    }
  }

  const selectAllDocs = () => {
    setAllDocsSelected(true)
    setSelectedDocIds([])
  }

  const handleProviderSwitch = (providerId: string) => {
    const provider = getProviderInfo(providerId)
    if (!provider) return
    const defaultModel = provider.models.find(m => m.recommended)?.id || provider.models[0].id
    setSelectedModel(defaultModel)
    setSelectedReasoning(getDefaultReasoningForProvider(providerId, selectedReasoning))
  }

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return

    if (documents.length === 0) {
      setError('No documents in knowledge base. Upload some documents first.')
      return
    }

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
    }

    setMessages(prev => [...prev, userMessage])
    setInputValue('')
    setIsLoading(true)
    setError('')

    try {
      const headers = await getHeaders()
      const body: any = {
        query: userMessage.content,
        model: selectedModel,
        reasoning_effort: selectedReasoning,
      }

      if (!allDocsSelected && selectedDocIds.length > 0) {
        body.file_ids = selectedDocIds
      }

      const response = await fetch(`${API_BASE}/v1/knowledge-base/query`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Query failed')
      }

      const data = await response.json()

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: data.answer || 'No response received.',
        sources: data.sources || [],
        model: data.requested_model || data.model,
        actualModel: data.model,
        timestamp: new Date(),
      }

      setMessages(prev => [...prev, assistantMessage])
    } catch (err: any) {
      setError(err.message || 'Failed to get response')
      console.error('Query error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const clearChat = () => {
    setMessages([])
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const hasDocuments = documents.length > 0

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      {/* Header */}
      <div className="border-b border-gray-100">
        {/* Title bar */}
        <div className="px-5 py-3 flex items-center justify-between bg-gradient-to-r from-gray-900 to-gray-800">
          <h3 className="text-base font-semibold text-white flex items-center">
            <Sparkles className="w-4 h-4 mr-2 text-yellow-400" />
            Knowledge Base Chat
          </h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={loadDocuments}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              title="Refresh documents"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingDocs ? 'animate-spin' : ''}`} />
            </button>
            {messages.length > 0 && (
              <button
                onClick={clearChat}
                className="text-xs text-gray-400 hover:text-white flex items-center px-2 py-1 hover:bg-white/10 rounded transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Configuration Area */}
        <div className="p-4 bg-gray-50/80 space-y-4">

          {/* Provider Selection — Card Style */}
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
              AI Provider
            </label>
            <div className="grid grid-cols-4 gap-2">
              {AI_PROVIDERS.map((provider) => {
                const styles = PROVIDER_STYLES[provider.id]
                const isSelected = currentProviderId === provider.id
                return (
                  <button
                    key={provider.id}
                    type="button"
                    onClick={() => handleProviderSwitch(provider.id)}
                    className={`relative group px-3 py-2.5 rounded-xl border-2 text-center transition-all duration-200 ${
                      isSelected
                        ? `${styles.pillActive} ${styles.pillActiveText} shadow-md ${styles.glow}`
                        : `${styles.pill} ${styles.pillText}`
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold transition-colors ${
                        isSelected ? `bg-gradient-to-br ${styles.gradient} text-white shadow-sm` : `${styles.iconBg} ${styles.iconText}`
                      }`}>
                        {provider.icon}
                      </span>
                      <span className="text-sm font-medium">{provider.name}</span>
                    </div>
                    {!provider.available && (
                      <span className="block text-[9px] mt-1 opacity-50 italic">Coming Soon</span>
                    )}
                    {isSelected && (
                      <div className={`absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-6 h-1 rounded-full bg-gradient-to-r ${styles.gradient}`} />
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Model + Reasoning Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Model Selection — Card Grid */}
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Model
              </label>
              <div className="space-y-1.5">
                {currentProvider?.models.map((model) => {
                  const isSelected = selectedModel === model.id
                  return (
                    <button
                      key={model.id}
                      type="button"
                      onClick={() => setSelectedModel(model.id)}
                      className={`w-full relative px-3.5 py-2.5 rounded-xl border-2 text-left transition-all duration-200 ${
                        isSelected
                          ? `${providerStyles.ring} bg-white shadow-md ${providerStyles.glow}`
                          : 'border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <span className={`w-2 h-2 rounded-full transition-colors ${isSelected ? providerStyles.dot : 'bg-gray-300'}`} />
                          <div>
                            <p className={`text-sm font-semibold ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                              {model.name}
                            </p>
                            <p className="text-[11px] text-gray-400">{model.description}</p>
                          </div>
                        </div>
                        {model.recommended && (
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${providerStyles.badge}`}>
                            Best
                          </span>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Reasoning Level */}
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Reasoning Level
              </label>
              <div className={`grid gap-2 ${reasoningLevels.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                {reasoningLevels.map((level) => {
                  const isSelected = selectedReasoning === level.id
                  const styles = REASONING_STYLES[level.id]
                  const isMinimal = level.id === 'low' || level.id === 'MINIMAL'
                  const isDeep = level.id === 'high' || level.id === 'HIGH'
                  return (
                    <button
                      key={level.id}
                      type="button"
                      onClick={() => setSelectedReasoning(level.id)}
                      className={`px-3 py-3.5 rounded-xl border-2 text-center transition-all duration-200 ${
                        isSelected
                          ? `${styles.cardActive} ${styles.textActive} shadow-md`
                          : `${styles.card} ${styles.text}`
                      }`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        {isMinimal && <Zap className={`w-4 h-4 ${isSelected ? styles.icon : 'text-gray-400'}`} />}
                        {!isMinimal && !isDeep && <Sparkles className={`w-4 h-4 ${isSelected ? styles.icon : 'text-gray-400'}`} />}
                        {isDeep && <Brain className={`w-4 h-4 ${isSelected ? styles.icon : 'text-gray-400'}`} />}
                        <p className="text-xs font-bold">{level.name}</p>
                        <p className="text-[10px] opacity-70 leading-tight">{level.description}</p>
                      </div>
                    </button>
                  )
                })}
              </div>

              {/* Document Filter (below reasoning) */}
              <div className="mt-4">
                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Documents
                </label>
                <div className="relative">
                  <button
                    onClick={() => setShowDocSelector(!showDocSelector)}
                    disabled={!hasDocuments}
                    className="w-full px-3.5 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-400 bg-white text-sm text-left flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed hover:border-gray-300 transition-colors"
                  >
                    <span className="truncate text-gray-700">
                      {!hasDocuments
                        ? 'No documents available'
                        : allDocsSelected
                        ? `All documents (${documents.length})`
                        : `${selectedDocIds.length} document${selectedDocIds.length !== 1 ? 's' : ''} selected`}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${showDocSelector ? 'rotate-180' : ''}`} />
                  </button>

                  {showDocSelector && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                      <div className="p-2 border-b border-gray-100">
                        <button
                          onClick={() => {
                            selectAllDocs()
                            setShowDocSelector(false)
                          }}
                          className={`w-full px-3 py-2 text-sm text-left rounded-lg flex items-center ${
                            allDocsSelected
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'hover:bg-gray-50 text-gray-700'
                          }`}
                        >
                          {allDocsSelected ? (
                            <CheckSquare className="w-4 h-4 mr-2" />
                          ) : (
                            <Square className="w-4 h-4 mr-2" />
                          )}
                          All Documents
                        </button>
                      </div>
                      <div className="p-2">
                        {documents.map(doc => (
                          <button
                            key={doc.document_id}
                            onClick={() => toggleDocSelection(doc.document_id)}
                            className={`w-full px-3 py-2 text-sm text-left rounded-lg flex items-center ${
                              !allDocsSelected && selectedDocIds.includes(doc.document_id)
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'hover:bg-gray-50 text-gray-700'
                            }`}
                          >
                            {!allDocsSelected && selectedDocIds.includes(doc.document_id) ? (
                              <CheckSquare className="w-4 h-4 mr-2 flex-shrink-0" />
                            ) : (
                              <Square className="w-4 h-4 mr-2 flex-shrink-0" />
                            )}
                            <div className="truncate">
                              <div className="font-medium truncate">{doc.name}</div>
                              <div className="text-xs text-gray-500">
                                {doc.category} &middot; {formatBytes(doc.size_bytes)}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                      <div className="p-2 border-t border-gray-100">
                        <button
                          onClick={() => setShowDocSelector(false)}
                          className="w-full px-3 py-2 text-sm text-center text-gray-600 hover:bg-gray-50 rounded-lg"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="h-[32rem] overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-lg shadow-yellow-200">
              <Bot className="w-7 h-7 text-white" />
            </div>
            <p className="text-sm font-medium text-gray-700">Ask a question about your knowledge base</p>
            <p className="text-xs text-gray-400 mt-1">
              {hasDocuments
                ? `${documents.length} documents ready to search`
                : 'Upload some documents to get started'}
            </p>
          </div>
        )}

        {messages.map(message => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl p-4 ${
                message.role === 'user'
                  ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-gray-900 shadow-md shadow-yellow-200/50'
                  : 'bg-white border border-gray-200 text-gray-700 shadow-sm'
              }`}
            >
              <div className="flex items-start space-x-2">
                {message.role === 'assistant' && (
                  <Bot className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>

                  {message.sources && message.sources.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs font-medium text-gray-500 mb-2">Sources:</p>
                      <div className="space-y-1">
                        {message.sources.map((source, idx) => (
                          <div
                            key={idx}
                            className="text-xs bg-gray-50 px-2 py-1 rounded flex items-center"
                          >
                            <FileText className="w-3 h-3 mr-1 text-gray-400" />
                            <span className="truncate">
                              {source.filename || source.file_id}
                            </span>
                            {source.score && (
                              <span className="ml-2 text-gray-400">
                                ({(source.score * 100).toFixed(0)}%)
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {message.role === 'assistant' && message.model && (
                    <div className="mt-2">
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                        {message.model}
                      </span>
                      {message.actualModel && message.actualModel !== message.model && (
                        <span className="ml-2 text-xs text-gray-400">
                          actual: {message.actualModel}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {message.role === 'user' && (
                  <User className="w-5 h-5 text-gray-800 mt-0.5 flex-shrink-0" />
                )}
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
              <div className="flex items-center space-x-2">
                <Bot className="w-5 h-5 text-yellow-500" />
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                <span className="text-sm text-gray-500">Searching and thinking...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Error Display */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-t border-red-100 flex items-center space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <span className="text-sm text-red-600">{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="w-4 h-4 text-red-400 hover:text-red-600" />
          </button>
        </div>
      )}

      {/* Input Area */}
      <div className="p-4 border-t border-gray-100 bg-white">
        <div className="flex items-end space-x-3">
          <div className="flex-1">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={hasDocuments ? "Ask a question..." : "Upload documents to get started..."}
              disabled={!hasDocuments || isLoading}
              rows={2}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 resize-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            />
          </div>
          <button
            onClick={handleSendMessage}
            disabled={!hasDocuments || !inputValue.trim() || isLoading}
            className="px-4 py-3 bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-gray-900 font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md shadow-yellow-200/50 hover:shadow-lg"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-400">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  )
}
